import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PageLayout from '@/components/feature/PageLayout';
import { Link } from 'react-router-dom';
import ROICalculator from './components/ROICalculator';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

function PageHero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-16 md:pt-36 md:pb-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20corporate%20pricing%20and%20finance%20background%20with%20subtle%20glowing%20emerald%20green%20and%20warm%20amber%20light%20rays%20suggesting%20value%20and%20transparency%2C%20deep%20navy%20blue%20tones%20with%20elegant%20geometric%20shapes%2C%20minimalist%20professional%20composition%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay%2C%20premium%20feel&width=1600&height=900&seq=pricing-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-price-tag-3-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Transparent Pricing</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Clear Pricing,
            <br />
            <span className="text-primary-400">No Surprises</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            Flexible engagement models with upfront rates. You only pay for the talent you need — no setup fees, no hidden costs, no long-term lock-in.
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}

function PricingCards() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const tiers = [
    {
      name: 'Staff Augmentation',
      tagline: 'Add individual engineers to your existing team',
      icon: 'ri-user-add-line',
      accent: 'bg-primary-500',
      border: 'border-primary-200',
      badge: 'Most Popular',
      badgeColor: 'bg-primary-500 text-white',
      priceRange: '$35 – $140',
      priceUnit: '/hour',
      description: 'Hire pre-vetted engineers who integrate directly into your workflows, standups, and sprint planning. Scale up or down with 2 weeks notice.',
      features: [
        'Pre-vetted, interview-ready engineers',
        'Direct management by your team leads',
        'Full-time (40h/week) or part-time (20h+)',
        '2-week cancellation notice',
        'Weekly timesheets & activity reports',
        'Same timezone overlap (4–8 hrs)',
        'Risk-free 2-week trial period',
        'Instant replacement guarantee',
      ],
      cta: 'Find Engineers',
      ctaLink: '/talent',
    },
    {
      name: 'Dedicated Team',
      tagline: 'A fully managed squad with a Tech Lead',
      icon: 'ri-team-fill',
      accent: 'bg-emerald-500',
      border: 'border-emerald-200',
      badge: 'Best Value',
      badgeColor: 'bg-emerald-500 text-white',
      priceRange: '$8,000+',
      priceUnit: '/month per engineer',
      description: 'Get a complete, autonomous development team led by an experienced Tech Lead. Perfect for long-term product builds and continuous delivery.',
      features: [
        'Fully managed team with Tech Lead',
        'Autonomous sprint planning & delivery',
        'Dedicated Slack / Teams channel',
        'Weekly status calls & demos',
        'CI/CD setup & code review included',
        'Scalable from 3 to 20+ engineers',
        'Monthly retainer with detailed invoicing',
        'Quarterly performance reviews',
      ],
      cta: 'Build a Team',
      ctaLink: '/how-it-works',
    },
    {
      name: 'Contract Hiring',
      tagline: 'Short-term or long-term project contracts',
      icon: 'ri-file-list-3-line',
      accent: 'bg-amber-500',
      border: 'border-amber-200',
      badge: 'Flexible',
      badgeColor: 'bg-amber-500 text-white',
      priceRange: 'Custom',
      priceUnit: 'quote',
      description: 'Fixed-scope or time-based contracts for specific deliverables. We scope the project, assemble the team, and deliver on deadline.',
      features: [
        'Fixed-price or time-and-materials',
        'Scope definition & milestone planning',
        'Project manager assigned',
        'Weekly deliverable tracking',
        'Post-launch support included (30 days)',
        'IP assignment & NDA included',
        'Flexible renewal & extension',
        'Detailed SOW before kickoff',
      ],
      cta: 'Request a Quote',
      ctaLink: '/#cta',
    },
    {
      name: 'Offshore Center',
      tagline: 'A scalable remote delivery hub',
      icon: 'ri-global-line',
      accent: 'bg-sky-500',
      border: 'border-sky-200',
      badge: 'Enterprise',
      badgeColor: 'bg-sky-500 text-white',
      priceRange: 'Custom',
      priceUnit: 'enterprise agreement',
      description: 'Establish a dedicated offshore development center with ongoing infrastructure, HR, and operational support. Ideal for enterprises scaling fast.',
      features: [
        'Dedicated office & infrastructure',
        'Full HR, payroll & compliance handled',
        'Custom branding & onboarding',
        'Security audits & SOC 2 alignment',
        'Multi-timezone coverage',
        'Dedicated account director',
        'SLA-backed response times',
        'Annual contract with quarterly reviews',
      ],
      cta: 'Talk to Sales',
      ctaLink: '/#cta',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 right-0 w-[600px] h-[600px] bg-primary-100/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 left-0 w-[500px] h-[500px] bg-emerald-50/30 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-14 md:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-calculator-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Engagement Models</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Choose Your
            <br />
            <span className="text-primary-600">Engagement Model</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Whether you need a single specialist or an entire product squad, we have a model that fits your budget and delivery goals.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-8">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ ...springTransition, duration: 0.7, delay: index * 0.1 }}
              className={`relative bg-white rounded-2xl md:rounded-3xl border-2 ${tier.border} hover:shadow-2xl hover:shadow-${tier.accent.replace('bg-', '')}/5 transition-all duration-500 flex flex-col overflow-hidden group`}
            >
              <div className="absolute top-4 right-4 z-10">
                <span className={`inline-block px-3 py-1 text-xs font-bold rounded-full ${tier.badgeColor}`}>
                  {tier.badge}
                </span>
              </div>

              <div className="p-6 md:p-8 pb-4">
                <div className={`w-12 h-12 flex items-center justify-center ${tier.accent} rounded-xl mb-5 shadow-lg shadow-${tier.accent.replace('bg-', '')}/20 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${tier.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-1">{tier.name}</h3>
                <p className="text-sm text-gray-500 mb-5">{tier.tagline}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl md:text-4xl font-bold text-navy-900">{tier.priceRange}</span>
                  <span className="text-sm text-gray-500 font-medium">{tier.priceUnit}</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mt-3">{tier.description}</p>
              </div>

              <div className="px-6 md:px-8">
                <div className="border-t border-gray-100"></div>
              </div>

              <div className="p-6 md:p-8 pt-5 flex-1">
                <ul className="space-y-3">
                  {tier.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-start gap-3">
                      <div className={`w-5 h-5 mt-0.5 flex items-center justify-center rounded-full ${tier.accent} flex-shrink-0`}>
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-sm text-gray-700 leading-snug">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 md:p-8 pt-0">
                <Link
                  to={tier.ctaLink}
                  className={`block w-full text-center px-6 py-3.5 ${tier.accent} text-white text-sm font-semibold rounded-xl hover:shadow-xl hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group/btn`}
                >
                  {tier.cta}
                  <i className="ri-arrow-right-line ml-1.5 group-hover/btn:translate-x-1 transition-transform duration-300 inline-block"></i>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RateBreakdown() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const levels = [
    {
      title: 'Junior Developer',
      experience: '1–2 years',
      rate: '$35 – $55',
      unit: '/hour',
      icon: 'ri-seedling-line',
      accent: 'bg-emerald-50 border-emerald-100',
      iconColor: 'text-emerald-600',
      skills: 'Great for MVPs, internal tools, testing, and UI implementation under senior guidance.',
    },
    {
      title: 'Mid-Level Developer',
      experience: '3–5 years',
      rate: '$55 – $85',
      unit: '/hour',
      icon: 'ri-plant-line',
      accent: 'bg-primary-50 border-primary-100',
      iconColor: 'text-primary-600',
      skills: 'Owns features end-to-end. Solid system design, code review, mentoring juniors.',
    },
    {
      title: 'Senior Developer',
      experience: '6–9 years',
      rate: '$85 – $120',
      unit: '/hour',
      icon: 'ri-tree-line',
      accent: 'bg-amber-50 border-amber-100',
      iconColor: 'text-amber-600',
      skills: 'Architects systems, leads code quality, drives technical decisions, mentors teams.',
    },
    {
      title: 'Lead / Architect',
      experience: '10+ years',
      rate: '$120 – $140',
      unit: '/hour',
      icon: 'ri-building-4-line',
      accent: 'bg-sky-50 border-sky-100',
      iconColor: 'text-sky-600',
      skills: 'Sets technical vision, designs infrastructure, manages stakeholder communication.',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-bar-chart-box-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Rate Transparency</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Rates by
            <span className="text-primary-600"> Experience Level</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Rates vary by seniority, technology specialty, and engagement model. All rates are fully transparent — you see exactly what the engineer earns and our management fee.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {levels.map((level, index) => (
            <motion.div
              key={level.title}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ ...springTransition, duration: 0.6, delay: index * 0.08 }}
              className={`bg-white rounded-2xl border p-6 md:p-7 hover:shadow-lg transition-all duration-300 ${level.accent}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${level.accent} flex-shrink-0`}>
                  <i className={`${level.icon} ${level.iconColor} text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-bold text-navy-900">{level.title}</h3>
                  <p className="text-xs text-gray-500">{level.experience}</p>
                </div>
              </div>
              <div className="mb-4">
                <span className="text-2xl md:text-3xl font-bold text-navy-900">{level.rate}</span>
                <span className="text-sm text-gray-500 ml-1">{level.unit}</span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{level.skills}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const pricingFAQQuestions = [
  {
    q: 'Are there any setup fees or hidden costs?',
    a: 'Absolutely none. No setup fees, no platform fees, no hidden charges. You only pay for the hours worked or the agreed monthly retainer. We provide detailed timesheets and activity reports weekly.',
  },
  {
    q: 'How does billing work for Staff Augmentation?',
    a: 'We bill monthly in arrears based on approved timesheets. Net-15 payment terms for most clients. Long-term engagements can request net-30. We accept wire transfers, ACH, and major credit cards.',
  },
  {
    q: 'Can I change engagement models mid-project?',
    a: 'Yes. Many clients start with staff augmentation to validate fit, then transition to a dedicated team model as their product grows. We make transitions seamless with no downtime.',
  },
  {
    q: 'What is included in the Dedicated Team monthly retainer?',
    a: 'The retainer covers the engineer salaries, Tech Lead management, weekly status calls, code review, CI/CD infrastructure, and our operational overhead. You get one consolidated invoice per month.',
  },
  {
    q: 'Do you offer discounts for long-term contracts?',
    a: 'Yes. Contracts of 6 months or longer receive preferential rates. Annual engagements can lock in rates and receive an additional discount on the management fee.',
  },
  {
    q: 'What happens if I need to pause or scale down?',
    a: 'Staff augmentation clients can pause with 2 weeks notice. Dedicated team contracts include a scaling clause that lets you adjust team size quarterly. We are built for flexibility.',
  },
];

const generatePricingFAQschema = () => {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": pricingFAQQuestions.map((q) => ({
      "@type": "Question",
      "name": q.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.a
      }
    }))
  });
};

function PricingFAQ() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-question-answer-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Pricing FAQ</span>
          </motion.div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4">
            Common Pricing
            <span className="text-primary-600"> Questions</span>
          </h2>
        </motion.div>

        <div className="space-y-4 md:space-y-5">
          {pricingFAQQuestions.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.06, ...springTransition }}
              className="bg-gray-50 rounded-2xl border border-gray-100 p-5 md:p-6 hover:border-primary-200 hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-primary-500 rounded-lg flex-shrink-0 shadow-lg shadow-primary-500/20">
                  <i className="ri-question-mark text-white text-sm md:text-base"></i>
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-bold text-navy-900 mb-2">{item.q}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
            <i className="ri-message-3-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Not Sure Which Model Fits?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6">
            Let Us Build a
            <span className="text-primary-400"> Custom Quote</span>
          </h2>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto mb-8 md:mb-10">
            Every project is different. Talk to our team and we will recommend the right model, team size, and stack for your budget and timeline.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Browse Talent Pool
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              See How It Works
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function PricingPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generatePricingFAQschema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <PageLayout
      title="Transparent Pricing — Engineering Talent & Teams"
      description="See Thoughtic Solutions' clear pricing for staff augmentation, dedicated teams, and offshore development. No hidden fees, flexible models, and risk-free trials."
      keywords="IT staffing pricing, remote developer rates, staff augmentation cost, dedicated team pricing, offshore development cost, software engineer hourly rate, engineering team pricing"
    >
      <PageHero />
      <PricingCards />
      <RateBreakdown />
      <ROICalculator />
      <PricingFAQ />
      <PricingCTA />
    </PageLayout>
  );
}